/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CommandBarButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { ContextualMenuItemType, DirectionalHint } from '@fluentui/react/lib/ContextualMenu';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useDocumentEventHandler } from 'src/core/hooks/useDocumentEventHandler';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { ctrlKey } from 'src/core/utils/keyboardUtils';
import { UtterancePredictionsStore } from 'src/luis/projects/utterances/hooks/useUtterances';
import { EntityUtteranceTableViewOptions } from 'src/luis/verticals/entityDetails/components/entityExamples/EntityExamples';

export type EntityUtterancesTableCommandBarProps = TableCommandBarProps<UtterancePredictionsStore> & {
    /**
     * If the confirm entity predictions command is disabled.
     */
    isConfirmPredictionsDisabled: boolean;

    /**
     * Current user view options.
     */
    viewOptions: EntityUtteranceTableViewOptions;

    /**
     * Current user view options.
     */
    onChangeViewOptions: (utterancesViewOption: Partial<EntityUtteranceTableViewOptions>) => void;

    /**
     * A callback for confirming predictions.
     */
    onConfirmPredictions: () => void;

    /**
     * A callback for deleting an utterance.
     */
    onDeleteUtterances: () => void;

    /**
     * A callback for editing an utterance.
     */
    onEditUtterance: () => void;

    /**
     * User Role to extract user actions.
     */
    userRole: string;

    /**
     * Role to display in the luis tool tip messages.
     */
    displayedUserRole: string;
};

const messages = defineMessages({
    confirmAllEntities: {
        id: 'newListEntityDetails.entityUtterancesTableCommandBar.confirmAllEntities',
        defaultMessage: 'Confirm all entities'
    },
    searchFieldPlaceholder: {
        id: 'newListEntityDetails.entityUtterancesTableCommandBar.searchFieldPlaceholder',
        defaultMessage: 'Search utterances...'
    },
    deleteButton: {
        id: 'newListEntityDetails.entityUtterancesTableCommandBar.deleteButton',
        defaultMessage: 'Delete'
    },
    editButton: {
        id: 'newListEntityDetails.entityUtterancesTableCommandBar.editButton',
        defaultMessage: 'Edit'
    },
    viewOptions: {
        id: 'newListEntityDetails.entityUtterancesTableCommandBar.viewOptions',
        defaultMessage: 'View options'
    },
    entities: {
        id: 'newListEntityDetails.entityUtterancesTableCommandBar.entities',
        defaultMessage: 'Entities'
    },
    showPredictions: {
        id: 'newListEntityDetails.entityUtterancesTableCommandBar.showPredictions',
        defaultMessage: 'Show predictions'
    },
    showNames: {
        id: 'newListEntityDetails.entityUtterancesTableCommandBar.showNames',
        defaultMessage: 'Show names'
    },
    showSubentities: {
        id: 'newListEntityDetails.entityUtterancesTableCommandBar.showSubentities',
        defaultMessage: 'Show subentities'
    },
    showPrebuiltAndRegex: {
        id: 'newListEntityDetails.entityUtterancesTableCommandBar.showPrebuiltAndRegex',
        defaultMessage: 'Show prebuilt and regex entities'
    }
});

/**
 * Renders a command bar for the utterances table
 * displayed in the entity details page.
 *
 * @param props The input properties for the command bar.
 */
export const EntityUtterancesTableCommandBar = (props: EntityUtterancesTableCommandBarProps) => {
    const {
        selectedItems = [],
        query = '',
        onQueryChange,
        viewOptions,
        onChangeViewOptions,
        onConfirmPredictions,
        onDeleteUtterances,
        onEditUtterance,
        isConfirmPredictionsDisabled,
        userRole,
        displayedUserRole
    } = props;
    const { localize } = useLocalization();

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    useDocumentEventHandler('keydown', event => {
        if (ctrlKey(event) && event.key.toLowerCase() === 'e') {
            event.preventDefault();
            if (event.shiftKey) {
                onChangeViewOptions({ hideSubEntities: !viewOptions.hideSubEntities });
            } else {
                onChangeViewOptions({ hideEntityNames: !viewOptions.hideEntityNames });
            }
        }
    });

    const commands: ICommandBarItemProps[] = [
        {
            key: 'confirmPredictions',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="confirmPredictionsButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canWrite || item.disabled}
                            onClick={onConfirmPredictions}
                            iconProps={{ iconName: 'CheckMark' }}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            ariaLabel={localize(messages.confirmAllEntities)}
                            name={localize(messages.confirmAllEntities)}
                            text={localize(messages.confirmAllEntities)}
                            data-automation-id="confirmPredictionsButton"
                        />
                    }
                />
            ),
            disabled: false
        },
        {
            key: 'delete',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canDeleteData || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="deleteButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canDeleteData || item.disabled}
                            onClick={onDeleteUtterances}
                            iconProps={{ iconName: 'Delete' }}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            ariaLabel={localize(messages.deleteButton)}
                            name={localize(messages.deleteButton)}
                            text={localize(messages.deleteButton)}
                            data-automation-id="deleteButton"
                        />
                    }
                />
            ),
            disabled: false
        },
        {
            key: 'edit',
            onRender: (item: ICommandBarItemProps) => (
                <RBACTooltip
                    hidden={canWrite || item.disabled}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="edtitButtonLuisToolTip"
                    componentSurroundedByToolTip={
                        <CommandBarButton
                            disabled={!canWrite || item.disabled}
                            onClick={onEditUtterance}
                            iconProps={{ iconName: 'Edit' }}
                            styles={getCommandBarButtonStyles(item.renderedInOverflow)}
                            ariaLabel={localize(messages.editButton)}
                            name={localize(messages.editButton)}
                            text={localize(messages.editButton)}
                            data-automation-id="editButton"
                        />
                    }
                />
            ),
            disabled: false
        }
    ];

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        [],
        ['confirmPredictions', 'delete', 'edit'],
        ['confirmPredictions', 'delete'],
        selectedItems.length
    );

    /**
     * Ensures that confirm all predictions command will be
     * disabled if there is no ML entities predicted.
     */
    if (isConfirmPredictionsDisabled) {
        items.find(c => c.key === 'confirmPredictions').disabled = true;
    }

    const farItems: ICommandBarItemProps[] = [
        {
            key: 'searchBox',
            onRender: () => (
                <LuisDebouncedCollapsableSearchBox
                    ariaLabel={localize(messages.searchFieldPlaceholder)}
                    size={36}
                    value={query}
                    placeholder={localize(messages.searchFieldPlaceholder)}
                    onChange={(_, newValue?: string) => onQueryChange(newValue)}
                />
            )
        },
        {
            key: 'viewOptions',
            name: localize(messages.viewOptions),
            ariaLabel: localize(messages.viewOptions),
            'data-automation-id': 'viewOptions',
            iconProps: { iconName: 'List' },
            subMenuProps: {
                directionalHint: DirectionalHint.bottomRightEdge,
                items: [
                    {
                        key: 'entities',
                        text: localize(messages.entities),
                        itemType: ContextualMenuItemType.Header
                    },
                    {
                        text: localize(messages.showPredictions),
                        ariaLabel: localize(messages.showPredictions),
                        key: 'showPredictions',
                        'data-automation-id': 'showPredictions',
                        canCheck: true,
                        checked: !viewOptions.hidePredictions,
                        onClick: () => onChangeViewOptions({ hidePredictions: !viewOptions.hidePredictions })
                    },
                    {
                        text: localize(messages.showNames),
                        secondaryText: 'Ctrl+E',
                        ariaLabel: localize(messages.showNames),
                        key: 'showNames',
                        'data-automation-id': 'showNames',
                        canCheck: true,
                        checked: !viewOptions.hideEntityNames,
                        onClick: () => onChangeViewOptions({ hideEntityNames: !viewOptions.hideEntityNames })
                    },
                    {
                        text: localize(messages.showSubentities),
                        secondaryText: 'Shift+Ctrl+E',
                        ariaLabel: localize(messages.showSubentities),
                        key: 'showSubentities',
                        'data-automation-id': 'showSubentities',
                        canCheck: true,
                        checked: !viewOptions.hideSubEntities,
                        onClick: () => onChangeViewOptions({ hideSubEntities: !viewOptions.hideSubEntities })
                    },
                    {
                        text: localize(messages.showPrebuiltAndRegex),
                        ariaLabel: localize(messages.showPrebuiltAndRegex),
                        key: 'showPrebuiltAndRegex',
                        'data-automation-id': 'showPrebuiltAndRegex',
                        canCheck: true,
                        checked: !viewOptions.hideRegexAndPrebuilt,
                        onClick: () => onChangeViewOptions({ hideRegexAndPrebuilt: !viewOptions.hideRegexAndPrebuilt })
                    }
                ]
            }
        }
    ];

    return <LuisCommandBar items={items} farItems={farItems} />;
};
